import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/hooks/useRealtime";
import { useBranding, LogoMark } from "@/context/BrandingContext";
import { EnvSwitcher } from "@/components/EnvSwitcher";
import { ProceduresProvider, useProcedures } from "@/context/ProceduresContext";
import { api } from "@/api";
import { confirmDialog } from "@/components/Confirm";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { flightDoctorState, untilFromTime } from "@/lib/flightDoctor";
import {
  LayoutDashboard, Siren, Truck, Map, Archive, Settings, Tablet, LogOut, ShieldCheck, Wifi, FileBarChart, BellRing, Tv, BookOpen, Send, ListChecks, PlaneTakeoff, PlaneLanding, Lock, Stethoscope,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", key: "nav.dashboard", icon: LayoutDashboard, roles: ["beheerder", "meldkamer"] },
  { to: "/incidenten", key: "nav.incidents", icon: Siren, roles: ["beheerder", "meldkamer"] },
  { to: "/taken", key: "nav.tasks", label: "Taken", icon: ListChecks, roles: ["beheerder", "meldkamer"] },
  { to: "/kluis", key: "nav.vault", label: "Kluis", icon: Lock, roles: ["beheerder", "meldkamer"] },
  { to: "/voertuigen", key: "nav.vehicles", icon: Truck, roles: ["beheerder", "meldkamer"] },
  { to: "/kaart", key: "nav.map", icon: Map, roles: ["beheerder", "meldkamer"] },
  { to: "/rapportage", key: "nav.reports", icon: FileBarChart, roles: ["beheerder", "meldkamer"] },
  { to: "/kennisbank", key: "nav.knowledgebase", icon: BookOpen, roles: ["beheerder", "meldkamer"] },
  { to: "/pageme", key: "nav.pageme", label: "PageMe", icon: Send, roles: ["beheerder", "meldkamer"] },
  { to: "/archief", key: "nav.archive", icon: Archive, roles: ["beheerder", "meldkamer"] },
];

function FlightRouteButtons() {
  const [cfg, setCfg] = useState(null);
  const load = () => api.get("/settings/map").then(({ data }) => setCfg(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useRealtime((m) => { if (m && m.type === "map_config_updated") load(); });
  const [tick, setTick] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setTick(new Date()), 15000); return () => clearInterval(t); }, []);
  const [askDoc, setAskDoc] = useState(false);
  const [docTime, setDocTime] = useState("");
  if (!cfg) return null;
  const doc = flightDoctorState(cfg.flight_doctor_present, cfg.flight_doctor_until, tick);
  const setDoctor = (present, until = null) => {
    setCfg((c) => ({ ...c, flight_doctor_present: present, flight_doctor_until: until }));
    api.post("/settings/flight-doctor", { present, until }).catch(load);
  };
  const leftName = cfg.flightroute_left_name || "Vliegroute links";
  const rightName = cfg.flightroute_right_name || "Vliegroute rechts";
  const choose = async (which, name) => {
    if (cfg.flightroute_active === which) return;
    const ok = await confirmDialog({ title: "Vliegroute wijzigen", message: `Weet je zeker dat je de actieve vliegroute wilt wijzigen naar "${name}"?`, confirmText: "Wijzigen" });
    if (!ok) return;
    setCfg((c) => ({ ...c, flightroute_active: which }));
    api.post("/settings/flightroute", { active: which }).catch(load);
  };
  const btn = (which, name, Icon) => (
    <button data-testid={`flightroute-${which}`} title={name} onClick={() => choose(which, name)}
      className={`flex flex-1 flex-col items-center gap-1 rounded-lg px-2 py-2 text-[11px] font-semibold transition-colors ${cfg.flightroute_active === which ? "bg-amber-500 text-slate-900 ring-2 ring-amber-300" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
      <Icon style={{ width: 18, height: 18 }} /><span className="w-full truncate text-center">{name}</span>
    </button>
  );
  return (
    <div className="px-3 pb-2" data-testid="flightroute-buttons">
      <div className="mb-1 px-1 text-[10px] uppercase tracking-widest text-slate-500">Vliegroute</div>
      <div className="flex gap-2">{btn("left", leftName, PlaneTakeoff)}{btn("right", rightName, PlaneLanding)}</div>
      <button data-testid="flight-doctor-toggle" title="Vlieger Arts in- of uitklokken"
        onClick={() => { if (doc.active) setDoctor(false); else { setDocTime(""); setAskDoc(true); } }}
        className={`mt-2 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-white transition-colors ${doc.active ? "bg-emerald-600 hover:bg-emerald-500" : "bg-red-600 hover:bg-red-500"}`}>
        <Stethoscope style={{ width: 18, height: 18 }} />
        <span className="flex-1 text-left">Vlieger Arts</span>
        <span data-testid="flight-doctor-state" className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] uppercase tracking-wide">{doc.label}</span>
      </button>
      <Dialog open={askDoc} onOpenChange={setAskDoc}>
        <DialogContent data-testid="flight-doctor-dialog" className="max-w-sm">
          <DialogHeader><DialogTitle>Vlieger Arts aanwezig</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); setDoctor(true, untilFromTime(docTime)); setAskDoc(false); }} className="space-y-3">
            <label className="block text-sm font-medium text-slate-700">Tot hoe laat aanwezig? <span className="font-normal text-slate-400">(optioneel)</span>
              <input data-testid="flight-doctor-time" type="time" autoFocus value={docTime} onChange={(e) => setDocTime(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-lg outline-none focus:border-emerald-500" />
            </label>
            <p className="text-xs text-slate-500">Op die tijd schakelt de Vlieger Arts automatisch naar afwezig. Leeg laten = aanwezig zonder eindtijd.</p>
            <button data-testid="flight-doctor-save" className="w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500">Aanwezig melden</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function CommandLayout({ children }) {
  return (
    <ProceduresProvider>
      <CommandLayoutInner>{children}</CommandLayoutInner>
    </ProceduresProvider>
  );
}

function CommandLayoutInner({ children }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [clock, setClock] = useState(new Date());
  const [lastMsgAt, setLastMsgAt] = useState(0);
  const [wsStale, setWsStale] = useState(false);
  const { hits, active, setOpen, keyword } = useProcedures();

  useRealtime(() => { setLastMsgAt(Date.now()); setWsStale(false); });
  const canVault = user && (user.role === "beheerder" || user.role === "meldkamer");
  const [vaultOut, setVaultOut] = useState(0);
  useEffect(() => {
    if (!canVault) return;
    const loadVault = () => api.get("/vault").then(({ data }) => setVaultOut(data.filter((v) => v.issued).length)).catch(() => {});
    loadVault();
    window.addEventListener("vault-refresh", loadVault);
    return () => window.removeEventListener("vault-refresh", loadVault);
  }, [canVault]);
  useRealtime((m) => { if (canVault && (m?.type === "vault_updated" || m?.type === "__ws_open")) window.dispatchEvent(new Event("vault-refresh")); });
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const t = setInterval(() => setWsStale(lastMsgAt > 0 && Date.now() - lastMsgAt > 35000), 5000);
    return () => clearInterval(t);
  }, [lastMsgAt]);
  const wsStatus = lastMsgAt === 0 ? "connecting" : wsStale ? "degraded" : "live";

  const items = NAV.filter((n) => n.roles.includes(user.role));

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Sidebar */}
      <aside className="no-print hidden w-64 shrink-0 flex-col bg-slate-900 text-slate-300 md:flex">
        <div className="flex items-center gap-2.5 border-b border-white/10 px-5 py-5">
          <LogoMark size={36} rounded="rounded-lg" />
          <div>
            <div className="font-display text-sm font-bold text-white">{branding.org_name}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{t("common.commandCenter")}</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-4">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
              {n.label || t(n.key)}
              {n.to === "/kluis" && vaultOut > 0 && (
                <span data-testid="nav-kluis-badge" title={`${vaultOut} uitgegeven`} className="ml-auto min-w-[1.25rem] rounded-full bg-red-600 px-1.5 py-0.5 text-center text-[11px] font-bold leading-none text-white">{vaultOut}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <FlightRouteButtons />
        {(user.role === "beheerder" || user.role === "meldkamer") && (
          <div className="px-3 pb-3">
            <button data-testid="nav-procedures" onClick={() => active && setOpen(true)} disabled={!active}
              title={active ? t("nav.proceduresTip") : t("nav.proceduresDisabled")}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                keyword && active
                  ? "bg-yellow-400 text-slate-900 ring-2 ring-yellow-300 animate-pulse"
                  : hits > 0 && active
                    ? "bg-amber-500 text-slate-900 ring-2 ring-amber-300 animate-pulse"
                    : active
                      ? "bg-white/10 text-white hover:bg-white/20"
                      : "cursor-not-allowed bg-white/5 text-slate-500"
              }`}>
              <BookOpen style={{ width: 18, height: 18 }} /> {t("nav.procedures")}
              {hits > 0 && active && (
                <span data-testid="nav-procedures-count" className="ml-auto rounded-full bg-slate-900/80 px-2 py-0.5 text-[11px] font-bold text-amber-300">{hits}</span>
              )}
            </button>
          </div>
        )}
        <div className="border-t border-white/10 p-3">
          <div className="mb-3 grid grid-cols-2 gap-2">
            {(user.role === "beheerder" || user.role === "meldkamer") && (
              <NavLink to="/beheer" data-testid="nav-beheer"
                className={({ isActive }) => `flex flex-col items-center gap-1 rounded-lg px-2 py-2.5 text-[11px] font-semibold transition-colors ${isActive ? "bg-blue-600 text-white" : "bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white"}`}>
                <Settings style={{ width: 18, height: 18 }} /> {t("nav.beheer")}
              </NavLink>
            )}
            <NavLink to="/voertuig" data-testid="nav-tablet"
              className="flex flex-col items-center gap-1 rounded-lg bg-white/5 px-2 py-2.5 text-[11px] font-semibold text-slate-300 transition-colors hover:bg-white/10 hover:text-white">
              <Tablet style={{ width: 18, height: 18 }} /> {t("nav.tablet")}
            </NavLink>
            <NavLink to="/pager" data-testid="nav-pager"
              className="flex flex-col items-center gap-1 rounded-lg bg-white/5 px-2 py-2.5 text-[11px] font-semibold text-slate-300 transition-colors hover:bg-white/10 hover:text-white">
              <BellRing style={{ width: 18, height: 18 }} /> {t("nav.pager")}
            </NavLink>
            <NavLink to="/kazerne" data-testid="nav-kazerne"
              className="flex flex-col items-center gap-1 rounded-lg bg-white/5 px-2 py-2.5 text-[11px] font-semibold text-slate-300 transition-colors hover:bg-white/10 hover:text-white">
              <Tv style={{ width: 18, height: 18 }} /> {t("nav.monitor")}
            </NavLink>
          </div>
          <div className="mb-2 flex items-center gap-2 px-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-700 text-xs font-bold text-white">
              {user.name?.charAt(0)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-semibold text-white">{user.name}</div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">{t(`role.${user.role}`)}</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); navigate("/login"); }}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 transition-colors hover:bg-white/5 hover:text-white"
          >
            <LogOut style={{ width: 16, height: 16 }} /> {t("common.logout")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print flex h-14 shrink-0 items-center justify-between border-b border-slate-200 bg-white/85 px-5 backdrop-blur">
          <div className="flex items-center gap-2 text-sm text-slate-500 md:hidden">
            <ShieldCheck className="h-5 w-5 text-blue-600" /> <span className="font-display font-bold text-slate-900">DispatchMe</span>
          </div>
          <div className="hidden font-mono text-sm text-slate-500 md:block">
            {clock.toLocaleString("nl-NL", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </div>
          <div className="flex items-center gap-2">
            {user.role === "meldkamer" && (
              <NavLink to="/beheer" data-testid="quick-control" className="no-print hidden items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 sm:inline-flex">
                <Settings style={{ width: 14, height: 14 }} /> {t("common.control")}
              </NavLink>
            )}
            <NavLink to="/voertuig" data-testid="quick-vehicle" className="no-print hidden items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 sm:inline-flex">
              <Truck style={{ width: 14, height: 14 }} /> {t("common.vehicleApp")}
            </NavLink>
            <NavLink to="/pager" data-testid="quick-pager" className="no-print hidden items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 sm:inline-flex">
              <BellRing style={{ width: 14, height: 14 }} /> {t("nav.pager")}
            </NavLink>
            <EnvSwitcher />
            <LanguageSwitcher persist />
            <span
              data-testid="ws-indicator"
              data-ws-status={wsStatus}
              title={wsStatus === "live" ? t("ws.liveTitle") : wsStatus === "degraded" ? t("ws.degradedTitle") : t("ws.connectingTitle")}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                wsStatus === "live" ? "bg-emerald-50 text-emerald-700" : wsStatus === "degraded" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${wsStatus === "live" ? "bg-emerald-500 live-dot" : wsStatus === "degraded" ? "bg-amber-500 live-dot" : "bg-slate-400"}`} />
              <Wifi style={{ width: 13, height: 13 }} /> {wsStatus === "live" ? t("ws.live") : wsStatus === "degraded" ? t("ws.reconnecting") : t("ws.connecting")}
            </span>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-5 lg:p-7">{children}</main>
      </div>
    </div>
  );
}
